import type { IncidentDto, MonitorDto, UptimeDay } from '@fos/shared/contracts/monitoring';
import { incidentDurationText } from '@fos/shared/labels';
import { NBSP } from '@fos/shared/money';
import { toLocalDate } from '@fos/shared/time';
import { formatDateShort, formatTime } from '../../lib/format';
import type { Tone } from '../../ui/Status';

/** Odcień dnia na pasku dostępności: 100% = zielony, drobne przerwy = pomarańczowy, awaria = czerwony. */
export function uptimeDayTone(day: Pick<UptimeDay, 'total' | 'ok'>): Tone | null {
  if (day.total === 0) return null;
  if (day.ok === day.total) return 'success';
  return day.ok / day.total >= 0.95 ? 'warning' : 'danger';
}

/** „12.09, 4 min” (Figma 08: „Ostatni problem”). */
export function lastProblemText(i: Pick<IncidentDto, 'openedAt' | 'durationSec'>, now = new Date()): string {
  return `${formatDateShort(i.openedAt, now)}, ${incidentDurationText(i.durationSec)}`;
}

/** „od 9:44” (dziś) albo „od 4.10, 9:44”. */
export function sinceText(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const sameDay = toLocalDate(d) === toLocalDate(now);
  return sameDay ? `od ${formatTime(d)}` : `od ${formatDateShort(d, now)}, ${formatTime(d)}`;
}

/** Zakres godzin odcinka osi czasu: „9:40” albo „9:40–9:44”. */
export function segmentRangeText(from: string, to: string): string {
  const a = formatTime(from);
  const b = formatTime(to);
  return a === b ? a : `${a}–${b}`;
}

export function latencyText(ms: number | null): string | null {
  return ms === null ? null : `${ms}${NBSP}ms`;
}

/** Interwał w formularzu: wartości w sekundach. */
export const INTERVAL_OPTIONS = [60, 120, 300, 600, 900, 1800, 3600] as const;
export const TIMEOUT_OPTIONS = [5_000, 10_000, 20_000, 30_000] as const;
export const THRESHOLD_OPTIONS = [1, 2, 3, 4, 5] as const;

/** Monitor sprawdzany (włączony) w trybie symulacji — przełącznik „Symuluj awarię / Przywróć”. */
export function isSimulated(m: Pick<MonitorDto, 'mode'> | null | undefined): boolean {
  return m?.mode === 'simulated';
}
